import { ChevronLeft, ChevronRight } from "lucide-react"

import { Boton } from "@/componentes/ui/Boton"
import { hoyLocal } from "@/lib/fechas"

/**
 * El período del gasto administrativo (28/10). Minuta del 27/10, punto 6: «el módulo debe
 * ajustarse de forma SEMANAL, con opción de 15, 30 y hasta 60 días». Arranca en la semana en
 * curso (lunes a domingo); las flechas mueven el período entero hacia atrás o adelante, sin
 * pasar de hoy. La regla de cada tipo vive en el servidor (`services/periodo_admin.py`); aquí
 * sólo se calcula la etiqueta.
 */

export type TipoPeriodo = "SEMANA" | "15" | "30" | "60" | "MES"
export type PeriodoAdmin = { tipo: TipoPeriodo; fecha: string }

const TIPOS: { valor: TipoPeriodo; etiqueta: string }[] = [
  { valor: "SEMANA", etiqueta: "Semana" },
  { valor: "15", etiqueta: "15 días" },
  { valor: "30", etiqueta: "30 días" },
  { valor: "60", etiqueta: "60 días" },
  { valor: "MES", etiqueta: "Mes" },
]

export const periodoInicial = (): PeriodoAdmin => ({ tipo: "SEMANA", fecha: hoyLocal() })

export const consultaDe = (p: PeriodoAdmin) => `periodo=${p.tipo}&fecha=${p.fecha}`

const aFecha = (iso: string) => {
  const [a, m, d] = iso.split("-").map(Number)
  return new Date(a ?? 2000, (m ?? 1) - 1, d ?? 1)
}
const aIso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
const corta = (d: Date) => d.toLocaleDateString("es-VE", { day: "numeric", month: "short" })

/** Desde y hasta del período, igual que el servidor. */
export function limitesAdmin(p: PeriodoAdmin): { desde: Date; hasta: Date } {
  const f = aFecha(p.fecha)
  if (p.tipo === "SEMANA") {
    const desde = new Date(f)
    desde.setDate(f.getDate() - ((f.getDay() + 6) % 7))
    const hasta = new Date(desde)
    hasta.setDate(desde.getDate() + 6)
    return { desde, hasta }
  }
  if (p.tipo === "MES") {
    return {
      desde: new Date(f.getFullYear(), f.getMonth(), 1),
      hasta: new Date(f.getFullYear(), f.getMonth() + 1, 0),
    }
  }
  const desde = new Date(f)
  desde.setDate(f.getDate() - Number(p.tipo) + 1)
  return { desde, hasta: f }
}

export function nombrePeriodo(p: PeriodoAdmin): string {
  const { desde, hasta } = limitesAdmin(p)
  if (p.tipo === "MES") return hasta.toLocaleDateString("es-VE", { month: "long", year: "numeric" })
  return `${corta(desde)} – ${corta(hasta)} ${hasta.getFullYear()}`
}

export function SelectorPeriodoAdmin({
  valor,
  onCambiar,
}: {
  valor: PeriodoAdmin
  onCambiar: (p: PeriodoAdmin) => void
}) {
  const hoy = aFecha(hoyLocal())
  const { hasta } = limitesAdmin(valor)

  function mover(pasos: number) {
    const f = aFecha(valor.fecha)
    if (valor.tipo === "MES") f.setMonth(f.getMonth() + pasos, 1)
    else f.setDate(f.getDate() + pasos * (valor.tipo === "SEMANA" ? 7 : Number(valor.tipo)))
    if (f > hoy) f.setTime(hoy.getTime())
    onCambiar({ ...valor, fecha: aIso(f) })
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <fieldset className="flex flex-wrap gap-1 border-0 p-0">
        <legend className="sr-only">Tipo de período</legend>
        {TIPOS.map((t) => (
          <Boton
            key={t.valor}
            tamano="sm"
            variante={valor.tipo === t.valor ? "primario" : "fantasma"}
            aria-pressed={valor.tipo === t.valor}
            onClick={() => onCambiar({ tipo: t.valor, fecha: hoyLocal() })}
          >
            {t.etiqueta}
          </Boton>
        ))}
      </fieldset>
      <div className="flex items-center gap-1">
        <Boton
          tamano="sm"
          variante="fantasma"
          onClick={() => mover(-1)}
          aria-label="Período anterior"
        >
          <ChevronLeft size={16} aria-hidden={true} />
        </Boton>
        <span className="min-w-[11rem] text-center text-sm font-medium tabular-nums">
          {nombrePeriodo(valor)}
        </span>
        <Boton
          tamano="sm"
          variante="fantasma"
          onClick={() => mover(1)}
          disabled={hasta >= hoy}
          aria-label="Período siguiente"
        >
          <ChevronRight size={16} aria-hidden={true} />
        </Boton>
      </div>
    </div>
  )
}
